import { t } from "@lingui/core/macro";
import { add, format, isValid, parse, sub } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { CalendarBody } from "@whoosh/design-system/src/components/controls/CalendarBody/components/CalendarBody";
import { ChevronMiniRight } from "@whoosh/design-system/src/components/icons_v2/general";
import { ChevronMiniLeft } from "@whoosh/design-system/src/components/icons_v2/general/icons/ChevronMiniLeft";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import {
  HStack,
  Pressable,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

export type MobileDateSwitcherProps = {
  startDate: string;
  endDate: string;
  selectedDate: string;
  onChange: (date: string) => void;
  clubWeekStartsOn: NumericalDayOfWeek;
};

export const MobileDateSwitcher = ({
  startDate,
  endDate,
  selectedDate,
  onChange,
  clubWeekStartsOn,
}: MobileDateSwitcherProps) => {
  const startDateValid = isValid(parse(startDate, "yyyy-MM-dd", new Date()));
  const endDateValid = isValid(parse(endDate, "yyyy-MM-dd", new Date()));
  const selectedDateValid = isValid(
    parse(selectedDate, "yyyy-MM-dd", new Date())
  );

  const [currentSelectedDate, setCurrentSelectedDate] = useState(selectedDate);

  const currentSelectedDatePretty = useMemo(() => {
    if (selectedDateValid) {
      return format(
        parse(currentSelectedDate, "yyyy-MM-dd", new Date()),
        "EEEE, MMM do"
      );
    } else {
      return "";
    }
  }, [currentSelectedDate, selectedDateValid]);

  const updateDate = useCallback(
    (date: Date) => {
      if (!isValid(date)) {
        return;
      }
      const newDateString = format(date, "yyyy-MM-dd");
      setCurrentSelectedDate(newDateString);
      if (newDateString !== selectedDate) {
        onChange(newDateString);
      }
    },
    [onChange, selectedDate]
  );

  useEffect(() => {
    if (!selectedDateValid) {
      return;
    }
    if (selectedDate !== currentSelectedDate) {
      setCurrentSelectedDate(selectedDate);
    }
  }, [currentSelectedDate, selectedDate, selectedDateValid]);

  if (!startDateValid || !endDateValid || !selectedDateValid) {
    return <Text>{t`Oh Golly somethings wrong with the date selection.`}</Text>;
  }

  const incrementSelectedDate = () => {
    const newDate = add(parse(currentSelectedDate, "yyyy-MM-dd", new Date()), {
      days: 1,
    });
    updateDate(newDate);
  };

  const decrementSelectedDate = () => {
    const newDate = sub(parse(currentSelectedDate, "yyyy-MM-dd", new Date()), {
      days: 1,
    });
    updateDate(newDate);
  };

  const currentDateObj = parse(currentSelectedDate, "yyyy-MM-dd", new Date());
  const startDateObj = parse(startDate, "yyyy-MM-dd", new Date());
  const endDateObj = parse(endDate, "yyyy-MM-dd", new Date());

  const leftDisabled = currentDateObj <= startDateObj;
  const rightDisabled = currentDateObj >= endDateObj;
  const allDisabled =
    currentDateObj < startDateObj || currentDateObj > endDateObj;

  return (
    <HStack
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      gap={6}
      paddingHorizontal={2}
      paddingTop={2}
    >
      <Pressable
        onPress={decrementSelectedDate}
        disabled={leftDisabled || allDisabled}
        height={32}
        width={32}
        borderRadius="2"
        backgroundColor={
          leftDisabled || allDisabled
            ? "button-disabled-surface-neutral"
            : "button-alt-surface-neutral"
        }
        justifyContent="center"
        alignItems="center"
        padding={2}
        accessibilityLabel={t`Previous date`}
        role="button"
        testID="mobile-date-switcher-left-button"
        _pressed={{
          opacity: 0.7,
        }}
      >
        <ChevronMiniLeft
          width={16}
          height={16}
          color={leftDisabled || allDisabled ? "icon-disabled" : "icon-primary"}
        />
      </Pressable>
      <Menu
        openPosition="bottom-center"
        trigger={({ onPress }) => {
          return (
            <Pressable
              onPress={onPress}
              disabled={allDisabled}
              accessibilityLabel={t`Select date`}
              role="button"
              testID="mobile-date-switcher-center-button"
            >
              <Text
                variant="mainSmallSemiBold"
                color={allDisabled ? "text-disabled" : "text-primary"}
                testID="mobile-date-switcher-display-date"
              >
                {currentSelectedDatePretty}
              </Text>
            </Pressable>
          );
        }}
        content={({ closeMenu }) => {
          return (
            <Menu.Content size="large">
              <CalendarBody
                startDate={startDate}
                endDate={endDate}
                selectedDate={currentSelectedDate}
                onSelectDate={(newDate) => {
                  updateDate(parse(newDate, "yyyy-MM-dd", new Date()));
                  onChange(newDate);
                  closeMenu();
                }}
                clubWeekStartsOn={clubWeekStartsOn}
              />
            </Menu.Content>
          );
        }}
      />
      <Pressable
        onPress={incrementSelectedDate}
        disabled={rightDisabled || allDisabled}
        height={32}
        width={32}
        borderRadius="2"
        backgroundColor={
          rightDisabled || allDisabled
            ? "button-disabled-surface-neutral"
            : "button-alt-surface-neutral"
        }
        justifyContent="center"
        alignItems="center"
        padding={2}
        accessibilityLabel={t`Next date`}
        role="button"
        testID="mobile-date-switcher-right-button"
        _pressed={{
          opacity: 0.7,
        }}
      >
        <ChevronMiniRight
          width={16}
          height={16}
          color={
            rightDisabled || allDisabled ? "icon-disabled" : "icon-primary"
          }
        />
      </Pressable>
    </HStack>
  );
};
